import {
  MIN_WAYPOINTS_COUNT_FOR_REORDER,
  MINIMAL_STATIONS_COUNT_FOR_ROUTE_REBUILD,
} from "../../constants";
import { WaypointCategoryEnum } from "../../enums";
import type {
  IEffectiveMlRouteIds,
  IResolveRecountedStationsInfoParams,
  IRoutingWaypointStationRow,
  TRoutingWaypointStationInput,
} from "../../models";
import { areStationRowsEqual } from "./routing-waypoint-station-row.helpers";
import type { TIsMatchingWaypoint } from "./routing-waypoint.models";

/**
 * Routing waypoints live in stationsInfo but are not passenger stops.
 * RideWaypoint / CourseWaypoint = user-owned (kept across station-set changes).
 * AreaWaypoint = derived from area settings on dynamic rides; synced when areas join/leave.
 * Dropped while a customized ML path is bound, and while ML is skipped because of ride-owned W.
 * Re-injected when that path is ignored/excluded in the ML form or the station set has no ML path.
 * MLPathWaypoint = copied from a customized station-route; replaced when that path / real-station set changes.
 * Detect W by waypointCategory, not by negative id (ids are synthetic but not the contract).
 */

/** Routing-only map point — not a passenger station (explicit waypointCategory). */
export const isStationRouteWaypointStation = (
  station?: TRoutingWaypointStationInput,
): boolean => Boolean(station?.waypointCategory);

/** Ride/course map waypoints owned by the user (not ML-path-derived, not area defaults). */
export const isUserOwnedRoutingWaypoint = (
  station?: TRoutingWaypointStationInput,
): boolean =>
  station?.waypointCategory === WaypointCategoryEnum.RideWaypoint ||
  station?.waypointCategory === WaypointCategoryEnum.CourseWaypoint;

/** Default waypoint copied from area settings onto a dynamic ride. */
export const isAreaRoutingWaypoint = (
  station?: TRoutingWaypointStationInput,
): boolean => station?.waypointCategory === WaypointCategoryEnum.AreaWaypoint;

/** Waypoint that came from a customized ML station-route path. */
export const isMlPathRoutingWaypoint = (
  station?: TRoutingWaypointStationInput,
): boolean => station?.waypointCategory === WaypointCategoryEnum.MLPathWaypoint;

export const filterRealRouteStations = <T extends IRoutingWaypointStationRow>(
  stations: T[] | undefined,
): T[] =>
  (stations ?? []).filter(station => !isStationRouteWaypointStation(station));

export const getRealRouteStationIds = (
  stations: ({ id: number } & IRoutingWaypointStationRow)[] | undefined,
): number[] => filterRealRouteStations(stations).map(station => station.id);

/** First/last passenger station — excludes routing map waypoints at route ends. */
export const getRealRouteEndpoints = <T extends TRoutingWaypointStationInput>(
  stations: T[] | undefined,
): { first?: T; last?: T } => {
  const realStations = (stations ?? []).filter(
    station => !isStationRouteWaypointStation(station),
  );

  return {
    first: realStations[0],
    last: realStations.at(-1),
  };
};

/**
 * Id lists have no category — W use negative synthetic ids, so id > 0 is the only filter.
 * Prefer filterRealRouteStations when the row (waypointCategory) is available.
 */
export const filterRealRouteStationIds = (
  stationIds: number[] | undefined,
): number[] => (stationIds || []).filter(stationId => stationId > 0);

/**
 * Fold routing-waypoint legs into the following real-station leg so distances/durations
 * stay aligned with a stripped passenger-station list (N_real - 1).
 */
export const collapseRouteLegsAcrossWaypoints = ({
  stations,
  legs,
}: {
  stations: TRoutingWaypointStationInput[] | undefined;
  legs: number[] | undefined;
}): number[] | undefined => {
  if (!legs?.length || !stations?.length) {
    return legs;
  }

  const collapsed: number[] = [];
  let pending = 0;
  let hasRealStation = false;

  stations.forEach((station, index) => {
    if (index > 0) {
      pending += legs[index - 1] ?? 0;
    }

    if (isStationRouteWaypointStation(station)) {
      return;
    }

    if (hasRealStation) {
      collapsed.push(pending);
      pending = 0;
    } else {
      hasRealStation = true;
    }
  });

  if (pending && collapsed.length) {
    collapsed[collapsed.length - 1] += pending;
  }

  return collapsed;
};

/** Spread passenger-station legs back onto the mixed list (last mixed leg of each gap holds the total). */
export const expandCollapsedRouteLegsAcrossWaypoints = ({
  stations,
  collapsedLegs,
}: {
  stations: TRoutingWaypointStationInput[] | undefined;
  collapsedLegs: (number | undefined)[] | undefined;
}): (number | undefined)[] | undefined => {
  if (!collapsedLegs || !stations?.length) {
    return collapsedLegs;
  }

  const mixedCount = Math.max(stations.length - 1, 0);
  const mixed: (number | undefined)[] = Array.from(
    { length: mixedCount },
    () => undefined,
  );
  let collapsedIndex = 0;
  let previousRealIndex = -1;

  stations.forEach((station, index) => {
    if (isStationRouteWaypointStation(station)) {
      return;
    }

    if (previousRealIndex >= 0) {
      const collapsed = collapsedLegs[collapsedIndex];
      collapsedIndex += 1;

      if (collapsed !== undefined) {
        for (
          let gapIndex = previousRealIndex;
          gapIndex < index - 1;
          gapIndex += 1
        ) {
          mixed[gapIndex] = 0;
        }
        mixed[index - 1] = collapsed;
      }
    }

    previousRealIndex = index;
  });

  return mixed;
};

/** Fold waypoint legs while keeping unknown (`undefined`) legs unknown. */
export const collapseOptionalRouteLegsAcrossWaypoints = ({
  stations,
  legs,
}: {
  stations: TRoutingWaypointStationInput[] | undefined;
  legs: (number | undefined)[] | undefined;
}): (number | undefined)[] | undefined => {
  if (!legs || !stations?.length) {
    return legs;
  }

  const collapsed: (number | undefined)[] = [];
  let pending = 0;
  let isPendingUnknown = false;
  let hasRealStation = false;

  stations.forEach((station, index) => {
    if (index > 0) {
      const leg = legs[index - 1];
      if (leg === undefined) {
        isPendingUnknown = true;
      } else {
        pending += leg;
      }
    }

    if (isStationRouteWaypointStation(station)) {
      return;
    }

    if (hasRealStation) {
      collapsed.push(isPendingUnknown ? undefined : pending);
      pending = 0;
      isPendingUnknown = false;
    } else {
      hasRealStation = true;
      pending = 0;
      isPendingUnknown = false;
    }
  });

  return collapsed;
};

export const routeHasRoutingWaypoints = (
  stations?: TRoutingWaypointStationInput[],
): boolean => (stations ?? []).some(isStationRouteWaypointStation);

export const routeHasUserOwnedRoutingWaypoints = (
  stations?: TRoutingWaypointStationInput[],
): boolean => (stations ?? []).some(isUserOwnedRoutingWaypoint);

/** Drop purple ML-path W rows; they are not ride-owned and must not survive a station-set change. */
export const dropMlPathRoutingWaypoints = <
  T extends IRoutingWaypointStationRow,
>(
  stations: T[] | undefined,
): T[] => (stations ?? []).filter(station => !isMlPathRoutingWaypoint(station));

/** Drop area-default W so a customized ML path (or course) is not mixed with derived area stops. */
export const dropAreaRoutingWaypoints = <T extends IRoutingWaypointStationRow>(
  stations: T[] | undefined,
): T[] => (stations ?? []).filter(station => !isAreaRoutingWaypoint(station));

/** Passenger-station id multiset — ML path W follows this set, not ride-owned rows. */
export const getRealRouteStationIdentityKey = <
  T extends { id?: number } & IRoutingWaypointStationRow,
>(
  stations: T[] | undefined,
): string =>
  (stations ?? [])
    .filter(station => !isStationRouteWaypointStation(station))
    .map(station => Number(station.id) || 0)
    .sort((left, right) => left - right)
    .join(",");

/** True when passenger-station ids (multiset) differ — ML path W must leave with the old set. */
export const haveRealRouteStationIdentitiesChanged = <
  T extends { id?: number } & IRoutingWaypointStationRow,
>({
  previousStations,
  nextStations,
}: {
  previousStations: T[] | undefined;
  nextStations: T[] | undefined;
}): boolean =>
  getRealRouteStationIdentityKey(previousStations) !==
  getRealRouteStationIdentityKey(nextStations);

/**
 * True when every routing waypoint from the request still appears in the ordered result.
 * Used to reject ML/system cache hits that strip newly added map waypoints.
 */
export const areAllSourceRoutingWaypointsPresent = <
  T extends { id: number } & IRoutingWaypointStationRow,
>({
  orderedStations,
  sourceStations,
}: {
  orderedStations: T[] | undefined;
  sourceStations: T[] | undefined;
}): boolean => {
  const orderedIds = new Set(
    (orderedStations ?? []).map(station => station.id),
  );
  const sourceWaypoints = (sourceStations ?? []).filter(
    isStationRouteWaypointStation,
  );

  if (!sourceWaypoints.length) {
    return true;
  }

  return sourceWaypoints.every(waypoint => orderedIds.has(waypoint.id));
};

/** Grid/station-route entity indicator — only from persisted waypointStops (category-stamped). */
export const stationRouteHasRoutingWaypoints = (stationRoute: {
  waypointIds?: number[];
  result?: { waypointStops?: unknown[] };
}): boolean => Boolean(stationRoute.result?.waypointStops?.length);

const hasSameStationOrder = <T extends IRoutingWaypointStationRow>(
  a: T[],
  b: T[],
): boolean =>
  a.length === b.length &&
  a.every((station, index) => areStationRowsEqual({ a: station, b: b[index] }));

const getWaypointSegmentAnchor = <T extends IRoutingWaypointStationRow>({
  stations,
  waypointIndex,
}: {
  stations: T[];
  waypointIndex: number;
}): string => {
  const prevReal = stations
    .slice(0, waypointIndex)
    .reverse()
    .find(station => !isStationRouteWaypointStation(station));
  const nextReal = stations
    .slice(waypointIndex + 1)
    .find(station => !isStationRouteWaypointStation(station));

  return `${prevReal?.id ?? "start"}:${prevReal?.secondaryId ?? 0}|${nextReal?.id ?? "end"}:${nextReal?.secondaryId ?? 0}`;
};

const getWaypointSegmentAnchorByRow = <T extends IRoutingWaypointStationRow>({
  stations,
  waypoint,
}: {
  stations: T[];
  waypoint: T;
}): string => {
  const waypointIndex = stations.findIndex(station =>
    areStationRowsEqual({ a: station, b: waypoint }),
  );

  return waypointIndex >= 0
    ? getWaypointSegmentAnchor({ stations, waypointIndex })
    : "";
};

/** Prefer source order when every W still sits in the same real-station gap after recount. */
const shouldPreferSourceStationOrder = <T extends IRoutingWaypointStationRow>({
  source,
  recounted,
}: {
  source: T[];
  recounted: T[];
}): boolean => {
  if (hasSameStationOrder(source, recounted)) {
    return false;
  }

  const sourceWaypoints = source.filter(isStationRouteWaypointStation);

  if (!sourceWaypoints.length) {
    return false;
  }

  const hasAllSourceWaypointsInRecounted = sourceWaypoints.every(waypoint =>
    recounted.some(station => areStationRowsEqual({ a: station, b: waypoint })),
  );

  if (!hasAllSourceWaypointsInRecounted) {
    return false;
  }

  return sourceWaypoints.every(
    waypoint =>
      getWaypointSegmentAnchorByRow({ stations: source, waypoint }) ===
      getWaypointSegmentAnchorByRow({ stations: recounted, waypoint }),
  );
};

/** True when two waypoints had different real-station gaps in source but share one gap after recount. */
const didRecountCollapseWaypointGaps = <T extends IRoutingWaypointStationRow>({
  source,
  recounted,
}: {
  source: T[];
  recounted: T[];
}): boolean => {
  const sourceWaypoints = source.filter(isStationRouteWaypointStation);

  if (sourceWaypoints.length < MIN_WAYPOINTS_COUNT_FOR_REORDER) {
    return false;
  }

  for (let left = 0; left < sourceWaypoints.length; left += 1) {
    for (let right = left + 1; right < sourceWaypoints.length; right += 1) {
      const leftWaypoint = sourceWaypoints[left];
      const rightWaypoint = sourceWaypoints[right];
      const leftSourceAnchor = getWaypointSegmentAnchorByRow({
        stations: source,
        waypoint: leftWaypoint,
      });
      const rightSourceAnchor = getWaypointSegmentAnchorByRow({
        stations: source,
        waypoint: rightWaypoint,
      });

      if (leftSourceAnchor === rightSourceAnchor) {
        continue;
      }

      const leftRecountedAnchor = getWaypointSegmentAnchorByRow({
        stations: recounted,
        waypoint: leftWaypoint,
      });
      const rightRecountedAnchor = getWaypointSegmentAnchorByRow({
        stations: recounted,
        waypoint: rightWaypoint,
      });

      if (
        leftRecountedAnchor &&
        rightRecountedAnchor &&
        leftRecountedAnchor === rightRecountedAnchor
      ) {
        return true;
      }
    }
  }

  return false;
};

/** ML path id used for waypoint merge — cache preferred over customized (matches ride metadata). */
export const getEffectiveMlRouteId = (
  routeIds: IEffectiveMlRouteIds,
): number | null =>
  routeIds.usedCachedRouteId ?? routeIds.customStationRouteId ?? null;

export const toEffectiveMlRouteIdsFromRideMetadata = (
  metadata?: Pick<
    IEffectiveMlRouteIds,
    "usedCachedRouteId" | "customStationRouteId"
  > | null,
): IEffectiveMlRouteIds => ({
  usedCachedRouteId: metadata?.usedCachedRouteId,
  customStationRouteId: metadata?.customStationRouteId,
});

export const toEffectiveMlRouteIdsFromRideRecount = (
  recounted?: Pick<
    IEffectiveMlRouteIds,
    "usedCachedRouteId" | "customStationRouteId"
  > | null,
): IEffectiveMlRouteIds => ({
  usedCachedRouteId: recounted?.usedCachedRouteId,
  customStationRouteId: recounted?.customStationRouteId,
});

const getLastRealStationIndex = <T extends TRoutingWaypointStationInput>(
  stations: T[],
): number => {
  for (let index = stations.length - 1; index >= 0; index -= 1) {
    if (!isStationRouteWaypointStation(stations[index])) {
      return index;
    }
  }

  return -1;
};

/**
 * Place a waypoint in the gap between the nearest source real stations that still exist
 * after a passenger/station drop. Falling back to index 1 clustered every orphaned W at 1–2.
 */
const insertWaypointUsingRemainingAnchors = <
  T extends IRoutingWaypointStationRow,
>({
  sourceOrder,
  result,
  waypoint,
  siblingWaypointIds,
}: {
  sourceOrder: T[];
  result: T[];
  waypoint: T;
  siblingWaypointIds: Set<number>;
}): T[] => {
  if (!result.length) {
    return [waypoint];
  }

  const firstRealIndex = result.findIndex(
    station => !isStationRouteWaypointStation(station),
  );
  const lastRealIndex = getLastRealStationIndex(result);

  if (firstRealIndex < 0 || lastRealIndex <= firstRealIndex) {
    return [...result, waypoint];
  }

  const waypointIndex = sourceOrder.findIndex(station =>
    areStationRowsEqual({ a: station, b: waypoint }),
  );
  const sourceIndex = waypointIndex >= 0 ? waypointIndex : 0;
  const isRemainingReal = (station: T): boolean =>
    !isStationRouteWaypointStation(station) &&
    result.some(row => areStationRowsEqual({ a: row, b: station }));

  const prevRemaining = sourceOrder
    .slice(0, sourceIndex)
    .reverse()
    .find(isRemainingReal);
  const nextRemaining = sourceOrder
    .slice(sourceIndex + 1)
    .find(isRemainingReal);

  let insertAt: number;

  if (prevRemaining) {
    const prevIndex = result.findIndex(station =>
      areStationRowsEqual({ a: station, b: prevRemaining }),
    );
    insertAt = prevIndex + 1;
    const nextIndex = nextRemaining
      ? result.findIndex(station =>
          areStationRowsEqual({ a: station, b: nextRemaining }),
        )
      : lastRealIndex;
    const gapEnd = nextIndex > prevIndex ? nextIndex : lastRealIndex;

    // Keep relative order of several W that shared the same real-station gap.
    while (
      insertAt < gapEnd &&
      isStationRouteWaypointStation(result[insertAt]) &&
      siblingWaypointIds.has(result[insertAt].id)
    ) {
      insertAt += 1;
    }

    insertAt = Math.min(insertAt, gapEnd);
  } else if (nextRemaining) {
    insertAt = result.findIndex(station =>
      areStationRowsEqual({ a: station, b: nextRemaining }),
    );
  } else {
    insertAt = firstRealIndex + 1;
  }

  insertAt = Math.max(firstRealIndex + 1, Math.min(insertAt, lastRealIndex));

  return [...result.slice(0, insertAt), waypoint, ...result.slice(insertAt)];
};

/**
 * Re-insert matching waypoints from original order after passenger-based reorder
 * (waypoints are not tied to passenger from/to and are dropped by sort otherwise).
 */
const preserveMatchingRoutingWaypointsInStationOrder = <
  T extends IRoutingWaypointStationRow,
>({
  sourceOrder,
  sortedStations,
  isMatchingWaypoint,
}: {
  sourceOrder: T[];
  sortedStations: T[];
  isMatchingWaypoint: TIsMatchingWaypoint;
}): T[] => {
  const waypoints = sourceOrder.filter(isMatchingWaypoint);

  if (!waypoints.length) {
    return sortedStations.filter(station => !isMatchingWaypoint(station));
  }

  let result = sortedStations.filter(station => !isMatchingWaypoint(station));
  const siblingWaypointIds = new Set(waypoints.map(waypoint => waypoint.id));

  for (const waypoint of waypoints) {
    result = insertWaypointUsingRemainingAnchors({
      sourceOrder,
      result,
      waypoint,
      siblingWaypointIds,
    });
  }

  return clampRoutingWaypointsAwayFromEnds(result);
};

/**
 * Re-insert routing waypoints from original order after passenger-based reorder
 * (waypoints are not tied to passenger from/to and are dropped by sort otherwise).
 */
export const preserveRoutingWaypointsInStationOrder = <
  T extends IRoutingWaypointStationRow,
>({
  sourceOrder,
  sortedStations,
}: {
  sourceOrder: T[];
  sortedStations: T[];
}): T[] =>
  preserveMatchingRoutingWaypointsInStationOrder({
    sourceOrder,
    sortedStations,
    isMatchingWaypoint: isStationRouteWaypointStation,
  });

/**
 * MLPath W belongs to the path found by passenger stations.
 * Same real-station set: keep dest waypoints. Station set changed: keep dest user-owned only.
 * Never copy waypoints from another ride — source W stay on the source; ML path W come from routing.
 */
export const preserveRideRoutingWaypointsForPassengerStationChange = <
  T extends IRoutingWaypointStationRow,
>({
  previousStations,
  nextStations,
}: {
  previousStations: T[];
  nextStations: T[];
}): T[] => {
  if (
    !haveRealRouteStationIdentitiesChanged({ previousStations, nextStations })
  ) {
    // Same passenger stations: ML path W still belongs to this set (e.g. passenger add on existing stops).
    return preserveRoutingWaypointsInStationOrder({
      sourceOrder: previousStations,
      sortedStations: nextStations,
    });
  }

  // Station set changed: drop ML path W so recount can attach W from the path found by the new stations.
  return preserveMatchingRoutingWaypointsInStationOrder({
    sourceOrder: previousStations,
    sortedStations: dropMlPathRoutingWaypoints(nextStations),
    isMatchingWaypoint: isUserOwnedRoutingWaypoint,
  });
};

/**
 * When ML path id changed: keep recounted (new MLPath waypoints) and re-insert only user-owned
 * waypoints from source (do not re-insert old MLPathWaypoint rows).
 */
const preserveUserOwnedRoutingWaypointsIntoRecounted = <
  T extends IRoutingWaypointStationRow,
>({
  sourceOrder,
  recounted,
}: {
  sourceOrder: T[];
  recounted: T[];
}): T[] => {
  const userOwnedWaypoints = sourceOrder.filter(isUserOwnedRoutingWaypoint);

  if (!userOwnedWaypoints.length) {
    return recounted;
  }

  // Drop any user-owned already in recounted, keep MLPath + real stations from new path.
  let result = recounted.filter(
    station => !isUserOwnedRoutingWaypoint(station),
  );
  const siblingWaypointIds = new Set(
    userOwnedWaypoints.map(waypoint => waypoint.id),
  );

  for (const waypoint of userOwnedWaypoints) {
    result = insertWaypointUsingRemainingAnchors({
      sourceOrder,
      result,
      waypoint,
      siblingWaypointIds,
    });
  }

  return clampRoutingWaypointsAwayFromEnds(result);
};

/** Move end waypoints inward until first/last are passenger stations. No-op when fewer than 2 reals. */
const clampRoutingWaypointsAwayFromEnds = <
  T extends IRoutingWaypointStationRow,
>(
  stations: T[],
): T[] => {
  const realCount = stations.filter(
    station => !isStationRouteWaypointStation(station),
  ).length;

  if (realCount < MINIMAL_STATIONS_COUNT_FOR_ROUTE_REBUILD) {
    return [...stations];
  }

  let result = [...stations];
  const firstRealIndex = result.findIndex(
    station => !isStationRouteWaypointStation(station),
  );

  if (firstRealIndex > 0) {
    const leadingWaypoints = result.slice(0, firstRealIndex);
    result = [
      result[firstRealIndex],
      ...leadingWaypoints,
      ...result.slice(firstRealIndex + 1),
    ];
  }

  let lastRealIndex = -1;
  for (let index = result.length - 1; index >= 0; index -= 1) {
    if (!isStationRouteWaypointStation(result[index])) {
      lastRealIndex = index;
      break;
    }
  }

  if (lastRealIndex >= 0 && lastRealIndex < result.length - 1) {
    const trailingWaypoints = result.slice(lastRealIndex + 1);
    result = [
      ...result.slice(0, lastRealIndex),
      ...trailingWaypoints,
      result[lastRealIndex],
    ];
  }

  return result;
};

/** Pin locked real endpoints from source order — probe/full OSRM must not move them. */
export const enforceLockedRealEndpointsInStationOrder = <
  T extends IRoutingWaypointStationRow,
>({
  stationsOrdered,
  sourceStations,
  endpointLocks,
}: {
  stationsOrdered: T[];
  sourceStations: T[];
  endpointLocks: { isOriginLocked: boolean; isDestinationLocked: boolean };
}): T[] => {
  if (!endpointLocks.isOriginLocked && !endpointLocks.isDestinationLocked) {
    return stationsOrdered;
  }

  const { first: sourceFirst, last: sourceLast } =
    getRealRouteEndpoints(sourceStations);

  if (!sourceFirst || !sourceLast) {
    return stationsOrdered;
  }

  const middle = stationsOrdered.filter(station => {
    if (
      endpointLocks.isOriginLocked &&
      areStationRowsEqual({ a: station, b: sourceFirst })
    ) {
      return false;
    }

    if (
      endpointLocks.isDestinationLocked &&
      areStationRowsEqual({ a: station, b: sourceLast })
    ) {
      return false;
    }

    return true;
  });

  const firstRow =
    stationsOrdered.find(station =>
      areStationRowsEqual({ a: station, b: sourceFirst }),
    ) ?? sourceFirst;
  const lastRow =
    stationsOrdered.find(station =>
      areStationRowsEqual({ a: station, b: sourceLast }),
    ) ?? sourceLast;

  let result = [...middle];

  if (endpointLocks.isOriginLocked) {
    result = [
      firstRow,
      ...result.filter(
        station => !areStationRowsEqual({ a: station, b: firstRow }),
      ),
    ];
  }

  if (endpointLocks.isDestinationLocked) {
    result = [
      ...result.filter(
        station => !areStationRowsEqual({ a: station, b: lastRow }),
      ),
      lastRow,
    ];
  }

  return result;
};

/**
 * Like preserveRoutingWaypointsInStationOrder, but real station order (and endpoints)
 * come from a separate OSRM run without waypoints. Waypoints that bubbled to route
 * ends in the full optimization are re-inserted after origin / before destination.
 */
export const preserveRoutingWaypointsWithProbedRealEndpoints = <
  T extends IRoutingWaypointStationRow,
>({
  sourceOrder,
  probedRealStationOrder,
}: {
  sourceOrder: T[];
  probedRealStationOrder: T[];
}): T[] => {
  const waypoints = sourceOrder.filter(isStationRouteWaypointStation);

  if (!waypoints.length) {
    return probedRealStationOrder;
  }

  const realFirst = probedRealStationOrder[0];
  const realLast = probedRealStationOrder.at(-1);

  if (!realFirst || !realLast) {
    return probedRealStationOrder;
  }

  let result = [...probedRealStationOrder];
  const siblingWaypointIds = new Set(waypoints.map(waypoint => waypoint.id));

  const insertAfterSkippingSiblings = (
    insertAt: number,
    gapEnd: number,
    waypoint: T,
  ): void => {
    let at = insertAt;
    while (
      at < gapEnd &&
      isStationRouteWaypointStation(result[at]) &&
      siblingWaypointIds.has(result[at].id)
    ) {
      at += 1;
    }

    const firstRealIndex = result.findIndex(
      station => !isStationRouteWaypointStation(station),
    );
    const lastRealIndex = getLastRealStationIndex(result);
    at = Math.max(firstRealIndex + 1, Math.min(at, gapEnd, lastRealIndex));
    result = [...result.slice(0, at), waypoint, ...result.slice(at)];
  };

  for (const waypoint of waypoints) {
    const waypointIndex = sourceOrder.findIndex(station =>
      areStationRowsEqual({ a: station, b: waypoint }),
    );
    const prevReal = sourceOrder
      .slice(0, waypointIndex)
      .reverse()
      .find(station => !isStationRouteWaypointStation(station));
    const nextReal = sourceOrder
      .slice(waypointIndex + 1)
      .find(station => !isStationRouteWaypointStation(station));

    if (!prevReal) {
      const originIndex = result.findIndex(station =>
        areStationRowsEqual({ a: station, b: realFirst }),
      );

      if (originIndex >= 0) {
        insertAfterSkippingSiblings(
          originIndex + 1,
          getLastRealStationIndex(result),
          waypoint,
        );
      }

      continue;
    }

    if (!nextReal) {
      const destIndex = result.findIndex(station =>
        areStationRowsEqual({ a: station, b: realLast }),
      );

      if (destIndex >= 0) {
        insertAfterSkippingSiblings(destIndex, destIndex, waypoint);
      }

      continue;
    }

    const prevIndex = result.findIndex(station =>
      areStationRowsEqual({ a: station, b: prevReal }),
    );
    const nextAnchorIndex = result.findIndex(station =>
      areStationRowsEqual({ a: station, b: nextReal }),
    );

    if (prevIndex >= 0) {
      const gapEnd =
        nextAnchorIndex > prevIndex
          ? nextAnchorIndex
          : getLastRealStationIndex(result);
      insertAfterSkippingSiblings(prevIndex + 1, gapEnd, waypoint);
      continue;
    }

    insertAfterSkippingSiblings(nextAnchorIndex, nextAnchorIndex, waypoint);
  }

  return clampRoutingWaypointsAwayFromEnds(result);
};

/** True when first or last station in the list is a routing map waypoint. */
export const hasRoutingWaypointAtRouteEnds = <
  T extends TRoutingWaypointStationInput,
>(
  stations: T[] | undefined,
): boolean => {
  if (!stations?.length) {
    return false;
  }

  return (
    isStationRouteWaypointStation(stations[0]) ||
    isStationRouteWaypointStation(stations.at(-1))
  );
};

/**
 * Keep routing waypoints strictly between passenger endpoints.
 * If fewer than 2 real stations, drop waypoints — they cannot stay interior.
 */
export const ensureRoutingWaypointsAreInterior = <
  T extends IRoutingWaypointStationRow,
>(
  stations: T[] | undefined,
): T[] => {
  if (!stations?.length) {
    return stations ?? [];
  }

  const realStations = stations.filter(
    station => !isStationRouteWaypointStation(station),
  );
  const waypoints = stations.filter(isStationRouteWaypointStation);

  if (!waypoints.length) {
    return realStations;
  }

  if (realStations.length < MINIMAL_STATIONS_COUNT_FOR_ROUTE_REBUILD) {
    return realStations;
  }

  return preserveRoutingWaypointsWithProbedRealEndpoints({
    sourceOrder: stations,
    probedRealStationOrder: realStations,
  });
};

/**
 * User accepted a newly found customized ML path (manual rebuild or ride merge confirm).
 * All prior waypoints leave — take recounted stations as-is (new MLPath stops only).
 */
export const acceptNewCustomizedMlPathStationsInfo = <
  T extends IRoutingWaypointStationRow,
>(
  recounted: T[],
): T[] =>
  recounted.filter(
    station =>
      !isUserOwnedRoutingWaypoint(station) && !isAreaRoutingWaypoint(station),
  );

/**
 * Recount / routing often returns passenger stations only (cache ignores map W).
 * shouldAcceptNewCustomizedMlPath: user confirmed a new ML path — drop user-owned W, keep recounted as-is.
 * Else if ML path id or real-station set changed: keep recounted MLPath W, re-insert only user-owned from source.
 * Else same path + same reals: merge source W back if the cache omitted them (keep category).
 */
export const resolveRecountedStationsInfo = <
  T extends IRoutingWaypointStationRow,
>({
  recounted,
  source,
  sourceRouteIds,
  recountRouteIds,
  shouldAcceptNewCustomizedMlPath,
}: IResolveRecountedStationsInfoParams<T>): T[] => {
  if (shouldAcceptNewCustomizedMlPath) {
    return acceptNewCustomizedMlPathStationsInfo(recounted);
  }

  const sourceRouteId = getEffectiveMlRouteId(sourceRouteIds);
  const recountRouteId = getEffectiveMlRouteId(recountRouteIds);

  if (
    sourceRouteId !== recountRouteId ||
    haveRealRouteStationIdentitiesChanged({
      previousStations: source,
      nextStations: recounted,
    })
  ) {
    // New ML path or different passenger stations: do not merge old MLPath W back in.
    return preserveUserOwnedRoutingWaypointsIntoRecounted({
      sourceOrder: source,
      recounted,
    });
  }

  return mergeRoutingWaypointsIntoRecountedStationsInfo({ recounted, source });
};

/** Re-apply routing map waypoints from pre-rebuild rows after recount (ML cache may omit them). */
export const mergeRoutingWaypointsIntoRecountedStationsInfo = <
  T extends IRoutingWaypointStationRow,
>({
  recounted,
  source,
}: {
  recounted: T[];
  source: T[];
}): T[] => {
  if (!source.some(isStationRouteWaypointStation)) {
    return recounted;
  }

  const sourceWaypoints = source.filter(isStationRouteWaypointStation);
  const hasRecountedAllWaypoints = sourceWaypoints.every(waypoint =>
    recounted.some(station => areStationRowsEqual({ a: station, b: waypoint })),
  );

  if (
    hasRecountedAllWaypoints &&
    !shouldPreferSourceStationOrder({ source, recounted }) &&
    !didRecountCollapseWaypointGaps({ source, recounted })
  ) {
    const hasRealOrderChanged = !hasSameStationOrder(
      filterRealRouteStations(source),
      filterRealRouteStations(recounted),
    );

    if (hasRealOrderChanged && source.some(isUserOwnedRoutingWaypoint)) {
      return preserveUserOwnedRoutingWaypointsIntoRecounted({
        sourceOrder: source,
        recounted,
      });
    }

    // Real-station order unchanged but user-owned waypoints were drag-reordered — keep UI order.
    if (
      !hasRealOrderChanged &&
      source.some(isUserOwnedRoutingWaypoint) &&
      !hasSameStationOrder(source, recounted)
    ) {
      return preserveUserOwnedRoutingWaypointsIntoRecounted({
        sourceOrder: source,
        recounted,
      });
    }

    const remainingSource = [...source];
    return recounted.map(station => {
      const sourceIndex = remainingSource.findIndex(candidate =>
        areStationRowsEqual({ a: candidate, b: station }),
      );
      const sourceRow =
        sourceIndex >= 0
          ? remainingSource.splice(sourceIndex, 1)[0]
          : undefined;
      return sourceRow && isStationRouteWaypointStation(sourceRow)
        ? { ...station, waypointCategory: sourceRow.waypointCategory }
        : station;
    });
  }

  const remainingRecounted = [...recounted];
  const merged = source
    .map(sourceStation => {
      const recountedIndex = remainingRecounted.findIndex(recountedStation =>
        areStationRowsEqual({ a: recountedStation, b: sourceStation }),
      );
      if (recountedIndex >= 0) {
        const fromRecounted = remainingRecounted.splice(recountedIndex, 1)[0];
        return isStationRouteWaypointStation(sourceStation)
          ? {
              ...fromRecounted,
              waypointCategory: sourceStation.waypointCategory,
            }
          : fromRecounted;
      }

      if (isStationRouteWaypointStation(sourceStation)) {
        return sourceStation;
      }

      return undefined;
    })
    .filter((station): station is T => station !== undefined);

  remainingRecounted.forEach(recountedStation => {
    merged.push(recountedStation);
  });

  return merged.length ? merged : recounted;
};
